/**
 * The per-tab `···` menu (`specs/M4-V1-views.md` §2.2).
 *
 * Measured on the sandbox, entry for entry, because round 3's version was invented: monday's
 * menu is **Pin view · — · Rename view · Duplicate view · Share view · Lock view to restrict
 * edits · — · Reorder (for you only) ▸ · — · Delete view**. There is no shared/personal toggle
 * anywhere in it (q292).
 *
 * **P-085 puts *Set as default view* back**, as a registered deviation. `views.setDefault` was
 * implemented, tested and reachable from `/v2` with **no caller in the UI**, while
 * `/boards/:id` resolves to *last viewed → the board's default view → the first view*: on a
 * board whose default is the wrong view every member landed on the wrong view on their first
 * visit and nobody could correct it — and an imported monday board's `views.is_default` could
 * not be corrected either. It is hidden on a **personal** view, which cannot be a default, and
 * ticked on the view that already is. Whether monday's own menu really lacks the row is q291.
 *
 * **Round 10j adds two more registered deviations**, both for the same reason as P-085 — the
 * product has the feature, wired end to end, and no gesture reaches it:
 *
 *   - **P-153 · *Hide view***. `hidden` is a column on the view row, `viewsAdmin.hide` is a
 *     procedure, and `All ⌄` lists hidden views with a *Hidden* badge and is documented as "the
 *     only way back to one" (acceptance V4) — while the row that hides one was offered only on
 *     the **implicit** main table (`target.synthetic`), which every board this product creates
 *     seeds away. It is now offered on every view that is not the board's **default**, since
 *     hiding the view a board opens on would strand it. q534 asks what monday's own row says.
 *   - **P-144 · *View settings***. `ViewSettingsPane` — `[V1§8]`'s 320 px pane with the view's
 *     name, its type, its visibility and the *Locked* facts — was rendered by nothing at all.
 *     q529 asks which control monday opens its own from; until it is answered the tab menu is
 *     the conservative host, because it is the surface that owns every other per-view action.
 *
 * The **implicit main table** gets a different first group — *Pin view · Hide main table*, with
 * no divider between them — carries no *Lock view*, and greys out everything it cannot do
 * `[cap:sandbox-tab-menu-main-table.json]`. The panel is 220 × 291 with 204 × 32 items.
 */
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';

export interface ViewTabMenuTarget {
  viewId: string | null;
  name: string;
  /** P-085: a personal view can never be a board default, so the row is not offered on one. */
  personal?: boolean;
  /** The board's implicit "Main Table": no row yet, so most entries are unavailable. */
  synthetic: boolean;
  locked: boolean;
  pinned: boolean;
  published: boolean;
  isDefault: boolean;
}

export interface ViewTabMenuActions {
  onPin(pinned: boolean): void;
  /** P-144: `[V1§8]`'s view settings pane. */
  onSettings(): void;
  /** P-085: `views.setDefault` — the view `/boards/:id` opens on. */
  onSetDefault(): void;
  onRename(): void;
  onDuplicate(): void;
  onShare(): void;
  onLock(): void;
  onHide(): void;
  onReorder(method: 'back' | 'ahead' | 'first' | 'last'): void;
  onDelete(): void;
}

export const REORDER_ENTRIES = [
  { id: 'back', label: 'Move back' },
  { id: 'ahead', label: 'Move ahead' },
  { id: 'first', label: 'Move to first' },
  { id: 'last', label: 'Move to last' },
] as const;

export function viewTabMenuEntries(
  target: ViewTabMenuTarget,
  actions: ViewTabMenuActions,
): MenuEntry[] {
  const disabled = target.synthetic;
  const entries: MenuEntry[] = [
    {
      id: 'pin',
      label: target.pinned ? 'Unpin view' : 'Pin view',
      onSelect: () => actions.onPin(!target.pinned),
    },
  ];

  if (target.synthetic) {
    // No divider before it, exactly as monday renders the main table's menu.
    entries.push({ id: 'hide', label: 'Hide main table', onSelect: () => actions.onHide() });
  } else if (!target.isDefault) {
    /*
     * P-153. Same position as the synthetic case's row — Pin, then Hide — and offered on every
     * view but the board's default, which must stay reachable: `All ⌄` is the only way back to
     * a hidden view and a board whose default is hidden opens on nothing.
     */
    entries.push({
      id: 'hide',
      label: 'Hide view',
      'data-testid': 'view-menu-hide',
      onSelect: () => actions.onHide(),
    });
  }
  entries.push({ id: 'd1', label: '', divider: true });

  entries.push(
    { id: 'rename', label: 'Rename view', disabled, onSelect: () => actions.onRename() },
    { id: 'duplicate', label: 'Duplicate view', disabled, onSelect: () => actions.onDuplicate() },
    { id: 'share', label: 'Share view', onSelect: () => actions.onShare() },
  );
  if (!target.synthetic) {
    entries.push({
      id: 'lock',
      label: target.locked ? 'Unlock view' : 'Lock view to restrict edits',
      onSelect: () => actions.onLock(),
    });
    // P-085. Not offered on a personal view: monday cannot make one a board default either.
    if (!target.personal) {
      entries.push({
        id: 'set-default',
        label: 'Set as default view',
        'data-testid': 'view-menu-set-default',
        disabled: target.isDefault,
        ...(target.isDefault
          ? { hint: '✓', title: 'This is already the view this board opens on.' }
          : {}),
        onSelect: () => actions.onSetDefault(),
      });
    }
  }

  // P-144: the pane `[V1§8]` describes, on the surface that owns the view's other actions.
  if (!target.synthetic) {
    entries.push({
      id: 'settings',
      label: 'View settings',
      'data-testid': 'view-menu-settings',
      onSelect: () => actions.onSettings(),
    });
  }

  entries.push(
    { id: 'd2', label: '', divider: true },
    {
      id: 'reorder',
      label: 'Reorder (for you only)',
      disabled,
      submenu: REORDER_ENTRIES.map((entry) => ({
        id: entry.id,
        label: entry.label,
        onSelect: () => actions.onReorder(entry.id),
      })),
    },
    { id: 'd3', label: '', divider: true },
    {
      id: 'delete',
      label: 'Delete view',
      danger: true,
      // V3: the default view cannot be deleted while others remain.
      disabled: disabled || target.isDefault,
      onSelect: () => actions.onDelete(),
    },
  );
  return entries;
}

export function ViewTabMenu({
  target,
  actions,
  x,
  y,
  onClose,
}: {
  target: ViewTabMenuTarget;
  actions: ViewTabMenuActions;
  x: number;
  y: number;
  onClose(): void;
}) {
  return (
    <Menu
      entries={viewTabMenuEntries(target, actions)}
      x={x}
      y={y}
      width={220}
      label={`${target.name} options`}
      onClose={onClose}
    />
  );
}
